import moment from 'moment';
import constantVariable from '../constantVariable';

/**
 * Single source of frontend validation rules for Direct Appointment upload.
 * Backend validation remains authoritative for AWB eligibility and business status checks.
 */
export const UPLOAD_HEADERS = [
  'AWB',
  'Appointment Date',
  'Time (Slot)',
  'Reference ID',
  'Remark',
];

export const MANDATORY_UPLOAD_HEADERS = [
  'AWB',
  'Appointment Date',
];

export const SEARCH_EXPORT_HEADERS = [
  'Client Name',
  'Origin Branch',
  'Destination Branch',
  'AWB',
  'MPS',
  'PDD',
  'Consignee Name',
  'Appointment Date',
  'Appointment Time (Slot)',
  'Status',
  'Reference ID',
  'Remark',
  'Tagging Date',
  'Tagged By',
];

export const UPLOAD_HEADER_ALIASES = {
  awb: ['awb', 'shipping id', 'shippingid', 'parentawb'],
  appointmentDate: ['appointment date', 'appointment delivery date', 'appointmentdate'],
  appointmentTime: ['time (slot)', 'time slot', 'appointment time', 'timeslot'],
  referenceId: ['reference id', 'referenceid', 'reference'],
  remark: ['remark', 'remarks', 'comment', 'comments'],
};

export const VALIDATION_CONFIG = {
  maxBulkRows: constantVariable.APPOINMENT_DATE_B2BBULK_LIMIT || 500,
  referenceIdMaxLength: 30,
  remarkMaxLength: 300,
  futureWindowDays: 30,
  tomorrowTaggingCutoffHour: 19,
  allowedRemarkPattern: /^[a-zA-Z0-9\s.,\-_/()]*$/,
  timeSlotPattern: /^([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/,
  referenceIdPattern: /^[a-zA-Z0-9]+$/,
};

const ALLOWED_EXTENSIONS = ['csv', 'xls', 'xlsx'];

export function validateFileExtension(fileName) {
  if (!fileName) {
    return { valid: false, message: 'Please select a file to upload.' };
  }
  const extension = fileName.split('.').pop().toLowerCase();
  if (ALLOWED_EXTENSIONS.indexOf(extension) === -1) {
    return {
      valid: false,
      message: 'Please upload file with .csv, .xls, or .xlsx extensions.',
    };
  }
  return { valid: true, extension };
}

export function validateFileNotEmpty(file) {
  if (!file || file.size === 0) {
    return { valid: false, message: 'Uploaded file is empty.' };
  }
  return { valid: true };
}

function normalizeHeader(value) {
  return (value || '').toString().trim().toLowerCase();
}

function mapHeaderIndex(headers) {
  const normalized = headers.map(normalizeHeader);
  const indexMap = {};

  Object.keys(UPLOAD_HEADER_ALIASES).forEach((fieldKey) => {
    UPLOAD_HEADER_ALIASES[fieldKey].some((alias) => {
      const idx = normalized.indexOf(alias);
      if (idx >= 0) {
        indexMap[fieldKey] = idx;
        return true;
      }
      return false;
    });
  });

  return indexMap;
}

export function validateMandatoryHeaders(headers) {
  const indexMap = mapHeaderIndex(headers);
  const missing = [];

  if (indexMap.awb === undefined) missing.push('AWB');
  if (indexMap.appointmentDate === undefined) missing.push('Appointment Date');

  if (missing.length) {
    return {
      valid: false,
      message: 'Missing mandatory headers: ' + missing.join(', '),
      indexMap: null,
    };
  }

  return { valid: true, indexMap };
}

function parseDateValue(value) {
  const trimmed = (value || '').toString().trim();
  if (!trimmed) return null;

  const formats = ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY/MM/DD'];
  for (let i = 0; i < formats.length; i++) {
    const parsed = moment(trimmed, formats[i], true);
    if (parsed.isValid()) {
      return parsed;
    }
  }
  return null;
}

export function isTomorrowTaggingCutoffPassed(referenceMoment) {
  const now = referenceMoment || moment();
  return now.hour() >= VALIDATION_CONFIG.tomorrowTaggingCutoffHour;
}

export function isTomorrowAppointmentDateBlocked(dateValue, referenceMoment) {
  const parsed = parseDateValue(dateValue);
  if (!parsed) {
    if (dateValue instanceof Date) {
      const dateMoment = moment(dateValue);
      if (!dateMoment.isValid()) {
        return false;
      }
      return validateTomorrowTaggingCutoff(dateMoment, referenceMoment).valid === false;
    }
    return false;
  }

  return validateTomorrowTaggingCutoff(parsed, referenceMoment).valid === false;
}

export function validateTomorrowTaggingCutoff(parsedDate, referenceMoment) {
  const now = referenceMoment || moment();
  const tomorrow = moment().startOf('day').add(1, 'day');

  if (!parsedDate.isSame(tomorrow, 'day')) {
    return { valid: true };
  }

  if (isTomorrowTaggingCutoffPassed(now)) {
    return {
      valid: false,
      message: 'Tomorrow\'s appointment must be tagged before 7 PM.',
    };
  }

  return { valid: true };
}

export function validateAppointmentDate(dateValue) {
  const parsed = parseDateValue(dateValue);
  if (!parsed) {
    return { valid: false, message: 'Invalid Appointment Date format. Use YYYY-MM-DD.' };
  }

  const today = moment().startOf('day');
  if (!parsed.isAfter(today)) {
    return { valid: false, message: 'Appointment Date must be a future date.' };
  }

  const tomorrowCutoffResult = validateTomorrowTaggingCutoff(parsed);
  if (!tomorrowCutoffResult.valid) {
    return tomorrowCutoffResult;
  }

  const maxDate = moment().startOf('day').add(VALIDATION_CONFIG.futureWindowDays, 'days');
  if (parsed.isAfter(maxDate)) {
    return {
      valid: false,
      message: 'Appointment Date exceeds allowed future window of ' + VALIDATION_CONFIG.futureWindowDays + ' days.',
    };
  }

  if (parsed.day() === 0) {
    return { valid: false, message: 'Appointment Date cannot be on Sunday.' };
  }

  return { valid: true, normalizedDate: parsed.format('YYYY-MM-DD') };
}

function formatTimePartToHHmmss(timePart) {
  const trimmed = (timePart || '').toString().trim();
  if (!trimmed) {
    return null;
  }

  const parsed = moment(trimmed, ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'], true);
  if (!parsed.isValid()) {
    return null;
  }

  return parsed.format('HH:mm:ss');
}

export function normalizeAppointmentTimeSlot(value) {
  return formatTimePartToHHmmss(value);
}

export function parseAppointmentTimeSlot(appointmentTime) {
  return {
    timeSlot: normalizeAppointmentTimeSlot(appointmentTime) || '',
  };
}

export function validateTimeSlot(value) {
  const trimmed = (value || '').toString().trim();
  if (!trimmed) {
    return { valid: true, normalizedTime: '' };
  }

  const normalizedTime = normalizeAppointmentTimeSlot(trimmed);
  if (!normalizedTime) {
    return {
      valid: false,
      message: 'Time (Slot) must be in HH:mm:ss format (e.g. 10:20:00).',
    };
  }

  return { valid: true, normalizedTime: normalizedTime };
}

export function validateReferenceId(value) {
  const trimmed = (value || '').toString().trim();
  if (!trimmed) {
    return { valid: true, normalizedReferenceId: '' };
  }
  if (trimmed.length > VALIDATION_CONFIG.referenceIdMaxLength) {
    return {
      valid: false,
      message: 'Reference ID must not exceed ' + VALIDATION_CONFIG.referenceIdMaxLength + ' characters.',
    };
  }
  if (!VALIDATION_CONFIG.referenceIdPattern.test(trimmed)) {
    return { valid: false, message: 'Reference ID must be alphanumeric.' };
  }
  return { valid: true, normalizedReferenceId: trimmed };
}

export function validateRemark(value) {
  const trimmed = (value || '').toString().trim();
  if (!trimmed) {
    return { valid: true, normalizedRemark: '' };
  }
  if (trimmed.length > VALIDATION_CONFIG.remarkMaxLength) {
    return {
      valid: false,
      message: 'Remark must not exceed ' + VALIDATION_CONFIG.remarkMaxLength + ' characters.',
    };
  }
  if (!VALIDATION_CONFIG.allowedRemarkPattern.test(trimmed)) {
    return { valid: false, message: 'Remark contains invalid characters.' };
  }
  return { valid: true, normalizedRemark: trimmed };
}

export function validateAwb(value) {
  const trimmed = normalizeAwb(value);
  if (!trimmed) {
    return { valid: false, message: 'AWB is required.' };
  }
  return { valid: true, normalizedAwb: trimmed };
}

export function normalizeAwb(value) {
  return (value || '').toString().trim();
}

export function validateUploadRow(row, rowNumber, isDuplicateAwb) {
  const errors = [];

  const awbResult = validateAwb(row.awb);
  if (!awbResult.valid) {
    errors.push(awbResult.message);
  } else if (isDuplicateAwb) {
    errors.push('Duplicate AWB found in uploaded file.');
  }

  const dateResult = validateAppointmentDate(row.appointmentDate);
  if (!dateResult.valid) {
    errors.push(dateResult.message);
  }

  const timeResult = validateTimeSlot(row.appointmentTime);
  if (!timeResult.valid) {
    errors.push(timeResult.message);
  }

  const refResult = validateReferenceId(row.referenceId);
  if (!refResult.valid) {
    errors.push(refResult.message);
  }

  const remarkResult = validateRemark(row.remark);
  if (!remarkResult.valid) {
    errors.push(remarkResult.message);
  }

  if (errors.length) {
    return {
      valid: false,
      rowNumber,
      errors,
      record: row,
    };
  }

  return {
    valid: true,
    rowNumber,
    record: {
      awb: awbResult.normalizedAwb,
      appointmentDate: dateResult.normalizedDate,
      appointmentTime: timeResult.normalizedTime,
      referenceId: refResult.normalizedReferenceId,
      remark: remarkResult.normalizedRemark,
    },
  };
}

export function detectDuplicateAwbs(rows) {
  const counts = {};
  rows.forEach((row) => {
    const awb = normalizeAwb(row.awb);
    if (!awb) return;
    counts[awb] = (counts[awb] || 0) + 1;
  });

  const duplicates = {};
  Object.keys(counts).forEach((awb) => {
    if (counts[awb] > 1) {
      duplicates[awb] = counts[awb];
    }
  });
  return duplicates;
}

export function dedupeValidUploadRecords(validUploadRecords, invalidUploadRecords) {
  const seen = {};
  const dedupedValid = [];
  const extraInvalid = [];

  (validUploadRecords || []).forEach((record, index) => {
    const awb = normalizeAwb(record.awb);
    if (awb && seen[awb]) {
      extraInvalid.push({
        rowNumber: record.rowNumber || index + 2,
        awb: record.awb,
        appointmentDate: record.appointmentDate,
        appointmentTime: record.appointmentTime,
        referenceId: record.referenceId,
        remark: record.remark,
        errors: 'Duplicate AWB found in uploaded file.',
      });
      return;
    }

    if (awb) {
      seen[awb] = true;
    }
    dedupedValid.push(record);
  });

  return {
    validUploadRecords: dedupedValid,
    invalidUploadRecords: (invalidUploadRecords || []).concat(extraInvalid),
    duplicateCount: extraInvalid.length,
  };
}

export function validateParsedRows(rows) {
  const firstAwbRow = {};
  const validUploadRecords = [];
  const invalidUploadRecords = [];
  let duplicateCount = 0;

  rows.forEach((row) => {
    const awb = normalizeAwb(row.awb);
    let isDuplicateAwb = false;

    if (awb) {
      if (firstAwbRow[awb] !== undefined) {
        isDuplicateAwb = true;
        duplicateCount++;
      } else {
        firstAwbRow[awb] = row.rowNumber;
      }
    }

    const result = validateUploadRow(row, row.rowNumber, isDuplicateAwb);
    if (result.valid) {
      validUploadRecords.push(result.record);
    } else {
      invalidUploadRecords.push({
        rowNumber: result.rowNumber,
        awb: row.awb,
        appointmentDate: row.appointmentDate,
        appointmentTime: row.appointmentTime,
        referenceId: row.referenceId,
        remark: row.remark,
        errors: result.errors.join('; '),
      });
    }
  });

  const deduped = dedupeValidUploadRecords(validUploadRecords, invalidUploadRecords);

  return {
    validUploadRecords: deduped.validUploadRecords,
    invalidUploadRecords: deduped.invalidUploadRecords,
    duplicateCount: duplicateCount + deduped.duplicateCount,
    duplicateAwbs: detectDuplicateAwbs(rows),
  };
}

function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result.map((cell) => cell.trim());
}

export function parseCsvFile(fileText) {
  const lines = fileText.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (!lines.length) {
    return { valid: false, message: 'Uploaded file has no data rows.' };
  }

  const headers = parseCsvLine(lines[0]);
  const headerValidation = validateMandatoryHeaders(headers);
  if (!headerValidation.valid) {
    return headerValidation;
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]);
    if (cells.every((cell) => cell === '')) continue;

    rows.push({
      rowNumber: i + 1,
      awb: cells[headerValidation.indexMap.awb],
      appointmentDate: cells[headerValidation.indexMap.appointmentDate],
      appointmentTime: headerValidation.indexMap.appointmentTime !== undefined
        ? cells[headerValidation.indexMap.appointmentTime]
        : '',
      referenceId: headerValidation.indexMap.referenceId !== undefined
        ? cells[headerValidation.indexMap.referenceId]
        : '',
      remark: headerValidation.indexMap.remark !== undefined
        ? cells[headerValidation.indexMap.remark]
        : '',
    });
  }

  if (!rows.length) {
    return { valid: false, message: 'Uploaded file has no data rows.' };
  }

  if (rows.length > VALIDATION_CONFIG.maxBulkRows) {
    return {
      valid: false,
      message: 'Please upload ' + VALIDATION_CONFIG.maxBulkRows + ' records or fewer.',
    };
  }

  return { valid: true, rows };
}

export function buildErrorCsvContent(invalidUploadRecords) {
  const header = UPLOAD_HEADERS.concat(['Row Number', 'Error']).join(',');
  const lines = invalidUploadRecords.map((row) => {
    return [
      row.awb || '',
      row.appointmentDate || '',
      row.appointmentTime || '',
      row.referenceId || '',
      (row.remark || '').replace(/,/g, ' '),
      row.rowNumber || '',
      (row.errors || '').replace(/,/g, ' '),
    ].join(',');
  });
  return [header].concat(lines).join('\n');
}

export function escapeCsvValue(value) {
  var str = value === null || value === undefined ? '' : String(value);
  if (str.indexOf(',') !== -1 || str.indexOf('"') !== -1 || str.indexOf('\n') !== -1 || str.indexOf('\r') !== -1) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

export function buildSearchResultCsvContent(records) {
  var header = SEARCH_EXPORT_HEADERS.join(',');
  var lines = records.map(function (row) {
    return [
      escapeCsvValue(row.clientName || row.businessaccountname || ''),
      escapeCsvValue(row.originBranch || row.originhubid || ''),
      escapeCsvValue(row.destinationBranch || row.finaldestinationid || ''),
      escapeCsvValue(row.awb || row.parentawb || ''),
      escapeCsvValue(row.mps || row.quantity || ''),
      escapeCsvValue(row.pickupslotsdate || ''),
      escapeCsvValue(row.consigneeName || row.consigneename || ''),
      escapeCsvValue(row.appointmentdate || row.appointmentDate || ''),
      escapeCsvValue(row.dropslotsdate || ''),
      escapeCsvValue(row.status || row.shipmentstatus || ''),
      escapeCsvValue(row.referenceId || row.referenceid || ''),
      escapeCsvValue(row.remark || ''),
      escapeCsvValue(row.createddate || row.createdDate || ''),
      escapeCsvValue(row.taggedBy || row.appointmenttaggedby || row.AppointmentTaggedBy || ''),
    ].join(',');
  });
  return [header].concat(lines).join('\n');
}

export function downloadTextFile(content, fileName) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}



// WEBPACK FOOTER //
// ./src/utils/directAppointmentValidation.js